import React, { useCallback } from 'react';
import { Button, message } from 'antd';
import { CloudUploadOutlined, CopyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useSocketContext } from '../../sockets/SocketContext';
import normalizeMktu from '../../utils/normalizeMktu';
import {
  collectSearchTabsSnapshot,
  dataUrlToRawBase64,
  hasSearchSnapshotContent,
} from '../../utils/collectSearchTabsSnapshot';

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

const SiderSearchSnapshotButton = () => {
  const tz = useSelector((s) => s.tz);
  const search = useSelector((s) => s.search);
  const globalSearch = useSelector((s) => s.globalSearch);
  const { createNewTaskOnServer } = useSocketContext();

  const buildSnapshot = useCallback(
    () => collectSearchTabsSnapshot({ tz, search, globalSearch }),
    [tz, search, globalSearch],
  );

  const handleCopy = useCallback(async () => {
    const snapshot = buildSnapshot();
    if (!hasSearchSnapshotContent(snapshot)) {
      message.info('Нет сохранённых результатов поиска для выгрузки');
      return;
    }
    const text = JSON.stringify(snapshot, null, 2);
    try {
      await copyTextToClipboard(text);
      message.success('Сводка поиска скопирована в буфер обмена');
    } catch {
      message.error('Не удалось скопировать в буфер обмена');
    }
  }, [buildSnapshot]);

  const handleSendAllData = useCallback(() => {
    const snapshot = buildSnapshot();
    if (!hasSearchSnapshotContent(snapshot)) {
      message.info('Нет сохранённых результатов поиска для отправки');
      return;
    }

    const imageForm = tz?.forms?.image;
    const quantity = imageForm?.quantity ?? 20;
    const imageName = imageForm?.fileName || 'search-snapshot';
    const mktuNorm = normalizeMktu(imageForm?.mktu);
    const mktu =
      mktuNorm == null ? [] : Array.isArray(mktuNorm) ? mktuNorm : [mktuNorm];

    const image = dataUrlToRawBase64(tz?.lastImageForReport) ?? '';

    const payload = {
      image,
      quantity,
      imageName,
      mktu,
      search_snapshot: snapshot,
    };

    const description = {
      getAiAnalitics: payload,
    };

    createNewTaskOnServer(description, 'getAiAnalitics', (err) => {
      if (err) {
        message.error(
          typeof err === 'string' ? err : 'Не удалось отправить сводку на сервер',
        );
        return;
      }
      message.success('Сводка поиска отправлена на сервер');
    });
  }, [buildSnapshot, createNewTaskOnServer, tz?.forms?.image, tz?.lastImageForReport]);

  return (
    <div className="app-sider-export-hover">
      <Button
        type="text"
        className="app-sider-export-btn"
        icon={<CloudUploadOutlined />}
        aria-label="Отправить сводку результатов поиска на сервер"
        title="Отправить сводку результатов поиска на сервер"
        onClick={handleSendAllData}
      />
      <Button
        type="text"
        className="app-sider-export-btn"
        icon={<CopyOutlined />}
        aria-label="Скопировать сводку результатов поиска (JSON)"
        title="Скопировать сводку результатов поиска (JSON)"
        onClick={handleCopy}
      />
    </div>
  );
};

export default SiderSearchSnapshotButton;
